import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Switch,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
  styled,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import './style.css';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enIN } from 'date-fns/locale';
import LeadStore from '../../store/LeadStore';
import dayjs from 'dayjs';
import { isEmpty } from '../../utils/helper';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const EditAdditionalServiceRequired = observer(({ leadId, details, handleEditAdditionalServiceRequired }) => {
  const theme = createTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const CustomFormControlLabel = styled(FormControlLabel)({
    '& .MuiFormControlLabel-label': {
      fontSize: '0.75rem',
      color: '#0009',
    },
  });

  const [formData, setFormData] = useState({
    id: details?.clientAdditionalServiceId ?? null,
    leadId,
    documentsToBeApplied: details?.documentsToBeApplied?.split(','),
    constructionApproval: details?.constructionApproval,
    liasioningHandledByName: details?.liasioningHandledByName,
    liasioningHandledByContactNumber: details?.liasioningHandledByContactNumber,
    marking: details?.marking ?? false,
    markingVendorName: details?.markingVendorName,
    markingAgencyName: details?.markingAgencyName,
    markingContactName: details?.markingContactName,
    markingExecutedDate: details?.markingExecutedDate,
    executionAndCasing: details?.executionAndCasing ?? false,
    executionAndCasingVendorName: details?.executionAndCasingVendorName,
    executionAndCasingAgencyName: details?.executionAndCasingAgencyName,
    executionAndCasingContactName: details?.executionAndCasingContactName,
    executionAndCasingExecutedDate: details?.executionAndCasingExecutedDate,
    borewellMotor: details?.borewellMotor ?? false,
    borewellMotorVendorName: details?.borewellMotorVendorName,
    borewellMotorAgencyName: details?.borewellMotorAgencyName,
    borewellMotorContactName: details?.borewellMotorContactName,
    borewellMotorExecutedDate: details?.borewellMotorExecutedDate,
    createdBy: details?.CreatedBy,
    createdDate: details?.createdDate,
    updatedBy: details?.updatedBy,
    updatedDate: details?.updatedDate,
  });

  const [errors, setErrors] = useState({
    documentsToBeApplied: false,
    constructionApproval: false,
    liasioningHandledByName: false,
    liasioningHandledByContactNumber: false,
    markingVendorName: false,
    markingAgencyName: false,
    markingContactName: false,
    markingExecutedDate: false,
    executionAndCasingVendorName: false,
    executionAndCasingAgencyName: false,
    executionAndCasingContactName: false,
    executionAndCasingExecutedDate: false,
    borewellMotorVendorName: false,
    borewellMotorAgencyName: false,
    borewellMotorContactName: false,
    borewellMotorExecutedDate: false,
  });

  const [errorMessages, setErrorMessages] = useState({
    documentsToBeApplied: '',
    constructionApproval: '',
    liasioningHandledByContactNumber: '',
  });

  const documentsToBeAppliedList = [
    { id: 1, name: 'Blue Print' },
    { id: 2, name: 'Tax Receipt' },
    { id: 3, name: 'Encumbrance Certificate' },
    { id: 4, name: 'Katha' },
    { id: 5, name: 'Tax Paid' },
    { id: 6, name: 'C D Patti' },
    { id: 7, name: 'Layout Plan' },
    { id: 8, name: 'Temaporary Water Connection' },
    { id: 9, name: 'Temaporary Electrical Connection' },
    { id: 10, name: 'Electrical Pole Shifting' },
    { id: 11, name: 'Demolition' },
    { id: 12, name: 'Bank Loan' },
  ];

  const constructionApprovalList = [
    { id: 1, name: 'MUDA' },
    { id: 2, name: 'Patana Panchayathi Bogadi/Sidlingapuru/Srirampura' },
    { id: 3, name: 'Grama Panchayathi' },
    { id: 4, name: 'BMRCL(Bangalore Metro Rail Corporation Limited)' },
    { id: 5, name: 'MCC(Mysore City Corporation)' },
    { id: 6, name: 'Hootagalli Nadakacheri' },
    { id: 999, name: 'Other' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'marking' || name === 'executionAndCasing' || name === 'borewellMotor') {
      let subFields = {};
      if (!e.target.checked) {
        subFields = {
          [`${name}VendorName`]: null,
          [`${name}AgencyName`]: null,
          [`${name}ContactName`]: null,
          [`${name}ExecutedDate`]: null,
        };
      }
      setFormData({
        ...formData,
        [name]: e.target.checked,
        ...subFields,
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    // Perform validation
    if (name === 'liasioningHandledByContactNumber') {
      setErrors({
        ...errors,
        [name]:
          formData.constructionApproval === 'Other' ? isContactNumberError(value, name) : isNumberError(value, name),
      });
    } else {
      setErrors({ ...errors, [name]: isEmpty(value) });
    }
  };

  const handleUpdateLeadKycSubmit = (e) => {
    e.preventDefault();

    const updatedErrors = {
      documentsToBeApplied: isEmpty(formData.documentsToBeApplied),
      constructionApproval: isEmpty(formData.constructionApproval),
      liasioningHandledByName: formData.constructionApproval === 'Other' && isEmpty(formData.liasioningHandledByName),
      liasioningHandledByContactNumber:
        formData.constructionApproval === 'Other'
          ? isContactNumberError(formData.liasioningHandledByContactNumber, 'liasioningHandledByContactNumber')
          : isNumberError(formData.liasioningHandledByContactNumber, 'liasioningHandledByContactNumber'),
      markingVendorName: formData.marking && isEmpty(formData.markingVendorName),
      markingAgencyName: formData.marking && isEmpty(formData.markingAgencyName),
      markingContactName: formData.marking && isEmpty(formData.markingContactName),
      markingExecutedDate: formData.marking && isEmpty(formData.markingExecutedDate),
      executionAndCasingVendorName: formData.executionAndCasing && isEmpty(formData.executionAndCasingVendorName),
      executionAndCasingAgencyName: formData.executionAndCasing && isEmpty(formData.executionAndCasingAgencyName),
      executionAndCasingContactName: formData.executionAndCasing && isEmpty(formData.executionAndCasingContactName),
      executionAndCasingExecutedDate: formData.executionAndCasing && isEmpty(formData.executionAndCasingExecutedDate),
      borewellMotorVendorName: formData.borewellMotor && isEmpty(formData.borewellMotorVendorName),
      borewellMotorAgencyName: formData.borewellMotor && isEmpty(formData.borewellMotorAgencyName),
      borewellMotorContactName: formData.borewellMotor && isEmpty(formData.borewellMotorContactName),
      borewellMotorExecutedDate: formData.borewellMotor && isEmpty(formData.borewellMotorExecutedDate),
    };
    setErrors(updatedErrors);

    if (!Object.values(updatedErrors).some((error) => error === true)) {
      const payload = { ...formData };
      payload.documentsToBeApplied = payload.documentsToBeApplied?.join(',');
      isEmpty(formData.id)
        ? LeadStore.createAdditionalServiceRequired(payload)
        : LeadStore.updateAdditionalServiceRequired(payload);
    }
  };

  useEffect(() => {
    LeadStore.updateAdditionalServiceRequiredStatus && handleEditAdditionalServiceRequired();
  }, [LeadStore.updateAdditionalServiceRequiredStatus]);

  const isContactNumberError = (value, name) => {
    if (isEmpty(value)) {
      return true;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  const isNumberError = (value, name) => {
    if (isEmpty(value)) {
      return false;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  return (
    <Paper sx={{ px: '30px', py: '20px', pb: '40px' }}>
      <Box
        id="lead-kyc-details-form"
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          handleUpdateLeadKycSubmit(event);
        }}
      >
        <Stack spacing={4}>
          <Stack spacing={3}>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <FormControl variant="standard" required error={Boolean(errors.documentsToBeApplied)}>
                    <InputLabel id="documentsToBeAppliedLabel" required shrink={true}>
                      Documents To Be Applied
                    </InputLabel>
                    <Select
                      labelId="documentsToBeAppliedLabel"
                      id="documentsToBeApplied"
                      name="documentsToBeApplied"
                      required
                      value={formData.documentsToBeApplied ?? []}
                      multiple
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          ['documentsToBeApplied']: typeof value === 'string' ? value.split(',') : value,
                        });
                        setErrors({
                          ...errors,
                          ['documentsToBeApplied']: isEmpty(value),
                        });
                      }}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {documentsToBeAppliedList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <Checkbox checked={formData.documentsToBeApplied?.indexOf(site.name) > -1} />
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="standard" required error={Boolean(errors.constructionApproval)}>
                    <InputLabel id="constructionApprovalLabel" required shrink={true}>
                      Construction Approval
                    </InputLabel>
                    <Select
                      labelId="constructionApprovalLabel"
                      id="constructionApproval"
                      name="constructionApproval"
                      required
                      value={formData.constructionApproval ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          ['constructionApproval']: value,
                          ['liasioningHandledByName']: value !== 'Other' ? null : formData.liasioningHandledByName,
                          ['liasioningHandledByContactNumber']:
                            value !== 'Other' ? '' : formData.liasioningHandledByContactNumber,
                        });
                        setErrors({
                          ...errors,
                          ['constructionApproval']: isEmpty(value),
                        });
                      }}
                      MenuProps={MenuProps}
                    >
                      {constructionApprovalList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.constructionApproval === 'Other' && (
                    <TextField
                      label="Liasioning Handled By Name"
                      id="liasioningHandledByName"
                      size="small"
                      variant="standard"
                      required={formData.constructionApproval === 'Other'}
                      name="liasioningHandledByName"
                      multiline
                      rows={1}
                      value={formData.liasioningHandledByName ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.liasioningHandledByName)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.constructionApproval === 'Other' && (
                    <TextField
                      label="Liasioning Handled By Contact Number"
                      id="liasioningHandledByContactNumber"
                      size="small"
                      variant="standard"
                      required={formData.constructionApproval === 'Other'}
                      name="liasioningHandledByContactNumber"
                      sx={{ fontSize: '12px' }}
                      value={formData.liasioningHandledByContactNumber ?? ''}
                      onChange={handleChange}
                      error={Boolean(errors.liasioningHandledByContactNumber)}
                      helperText={
                        Boolean(errors.liasioningHandledByContactNumber) &&
                        errorMessages.liasioningHandledByContactNumber
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  <CustomFormControlLabel
                    control={
                      <Switch
                        checked={formData.borewellMotor}
                        size="medium"
                        onChange={handleChange}
                        name="borewellMotor"
                      />
                    }
                    label="Borewell Motor"
                  />
                  {formData.borewellMotor && (
                    <TextField
                      label="Borewell Motor Vendor Name"
                      id="borewellMotorVendorName"
                      size="small"
                      variant="standard"
                      required={formData.borewellMotor}
                      name="borewellMotorVendorName"
                      multiline
                      rows={1}
                      value={formData.borewellMotorVendorName ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.borewellMotorVendorName)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.borewellMotor && (
                    <TextField
                      label="Borewell Motor Agency Name"
                      id="borewellMotorAgencyName"
                      size="small"
                      variant="standard"
                      required={formData.borewellMotor}
                      name="borewellMotorAgencyName"
                      multiline
                      rows={1}
                      value={formData.borewellMotorAgencyName ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.borewellMotorAgencyName)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.borewellMotor && (
                    <TextField
                      label="Borewell Motor Contact Name"
                      id="borewellMotorContactName"
                      size="small"
                      variant="standard"
                      required={formData.borewellMotor}
                      name="borewellMotorContactName"
                      multiline
                      rows={1}
                      value={formData.borewellMotorContactName ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.borewellMotorContactName)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.borewellMotor && (
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                      <DatePicker
                        label="Borewell Motor Executed Date"
                        required
                        value={formData.borewellMotorExecutedDate ? new Date(formData.borewellMotorExecutedDate) : null}
                        onChange={(newValue) => {
                          handleChange({
                            target: {
                              name: 'borewellMotorExecutedDate',
                              value: newValue,
                            },
                          });
                        }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            variant: 'standard',
                            name: 'borewellMotorExecutedDate',
                            fontSize: '12px',
                            required: formData.borewellMotor,
                            error: Boolean(errors.borewellMotorExecutedDate),
                            InputLabelProps: { shrink: true },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pl: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={1}>
                  <CustomFormControlLabel
                    control={<Switch checked={formData.marking} size="medium" onChange={handleChange} name="marking" />}
                    label="Borewell Marking"
                  />
                  {formData.marking && (
                    <TextField
                      label="Marking Vendor Name"
                      id="markingVendorName"
                      size="small"
                      variant="standard"
                      required={formData.marking}
                      name="markingVendorName"
                      multiline
                      rows={1}
                      value={formData.markingVendorName ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.markingVendorName)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.marking && (
                    <TextField
                      label="Marking Agency Name"
                      id="markingAgencyName"
                      size="small"
                      variant="standard"
                      required={formData.marking}
                      name="markingAgencyName"
                      multiline
                      rows={1}
                      value={formData.markingAgencyName ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.markingAgencyName)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.marking && (
                    <TextField
                      label="Marking Contact Name"
                      id="markingContactName"
                      size="small"
                      variant="standard"
                      required={formData.marking}
                      name="markingContactName"
                      multiline
                      rows={1}
                      value={formData.markingContactName ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.markingContactName)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.marking && (
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                      <DatePicker
                        label="Marking Executed Date"
                        required
                        value={formData.markingExecutedDate ? new Date(formData.markingExecutedDate) : null}
                        onChange={(newValue) => {
                          handleChange({
                            target: {
                              name: 'markingExecutedDate',
                              value: newValue,
                            },
                          });
                        }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            variant: 'standard',
                            name: 'markingExecutedDate',
                            fontSize: '12px',
                            required: formData.marking,
                            error: Boolean(errors.markingExecutedDate),
                            InputLabelProps: { shrink: true },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                  <CustomFormControlLabel
                    control={
                      <Switch
                        checked={formData.executionAndCasing}
                        size="medium"
                        onChange={handleChange}
                        name="executionAndCasing"
                      />
                    }
                    label="Execution And Casing"
                  />
                  {formData.executionAndCasing && (
                    <TextField
                      label="Execution And Casing Vendor Name"
                      id="executionAndCasingVendorName"
                      size="small"
                      variant="standard"
                      required={formData.executionAndCasing}
                      name="executionAndCasingVendorName"
                      multiline
                      rows={1}
                      value={formData.executionAndCasingVendorName ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.executionAndCasingVendorName)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.executionAndCasing && (
                    <TextField
                      label="Execution And Casing Agency Name"
                      id="executionAndCasingAgencyName"
                      size="small"
                      variant="standard"
                      required={formData.executionAndCasing}
                      name="executionAndCasingAgencyName"
                      multiline
                      rows={1}
                      value={formData.executionAndCasingAgencyName ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.executionAndCasingAgencyName)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.executionAndCasing && (
                    <TextField
                      label="Execution And Casing Contact Name"
                      id="executionAndCasingContactName"
                      size="small"
                      variant="standard"
                      required={formData.executionAndCasing}
                      name="executionAndCasingContactName"
                      multiline
                      rows={1}
                      value={formData.executionAndCasingContactName ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.executionAndCasingContactName)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.executionAndCasing && (
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                      <DatePicker
                        label="Execution And Casing Executed Date"
                        required
                        value={
                          formData.executionAndCasingExecutedDate
                            ? new Date(formData.executionAndCasingExecutedDate)
                            : null
                        }
                        onChange={(newValue) => {
                          handleChange({
                            target: {
                              name: 'executionAndCasingExecutedDate',
                              value: newValue,
                            },
                          });
                        }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            variant: 'standard',
                            name: 'executionAndCasingExecutedDate',
                            fontSize: '12px',
                            required: formData.executionAndCasing,
                            error: Boolean(errors.executionAndCasingExecutedDate),
                            InputLabelProps: { shrink: true },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack direction={'row'} sx={{ ml: 'auto' }} alignSelf={isSmallScreen ? '' : 'end'} spacing={2}>
            <Button
              variant="outlined"
              color="inherit"
              fullWidth
              size="small"
              onClick={() => handleEditAdditionalServiceRequired(false)}
            >
              Cancel
            </Button>
            <Button variant="contained" type="submit" fullWidth color="primary" size="small">
              Update
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
});
